import React, { useMemo } from 'react';
import { Edit, Star, ShieldOff, UserCog } from 'lucide-react';
import {
  Button,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  DataTable,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { User, Product } from '../types';
import { formatProductName } from '../utils';
import '../iam-support.styles.scss';

interface UserManagementProps {
  users: User[];
  products?: Product[];
  loading?: boolean;
  searchTerm?: string;
  onAssignPermission: (user: User) => void;
  onPromoteToSuperadmin: (user: User) => void;
  onRemovePermission: (user: User, product: string) => void;
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    pageSizeOptions?: number[];
  };
}

const UserManagement: React.FC<UserManagementProps> = ({
  users,
  products = [],
  loading = false,
  searchTerm = '',
  onAssignPermission,
  onPromoteToSuperadmin,
  onRemovePermission,
  pagination,
}) => {
  const dataTableColumns = useMemo((): TableColumn<User>[] => [
    {
      key: 'name',
      header: 'Name',
      accessor: 'name',
      render: (_v, row) => <span className="iam-support-name-cell font-medium">{row.name}</span>,
    },
    {
      key: 'email',
      header: 'Email',
      accessor: 'email',
      render: (_v, row) => <span className="text-gray-600">{row.email}</span>,
    },
    {
      key: 'role',
      header: 'Role',
      accessor: (row) => (row.isSuperadmin ? 'Superadmin' : 'User'),
      render: (_v, row) =>
        row.isSuperadmin ? (
          <Badge variant="default" className="iam-support-superadmin-badge bg-amber-500">
            Superadmin
          </Badge>
        ) : (
          <Badge variant="secondary">User</Badge>
        ),
    },
    {
      key: 'product_permissions',
      header: 'Product Permissions',
      accessor: (row) => Object.keys(row.permissions).length,
      render: (_v, row) => {
        const permissionCount = Object.keys(row.permissions).length;
        return (
          <div className="iam-support-permissions">
            {permissionCount > 0 ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="outline" className="cursor-help">
                      {permissionCount} product{permissionCount !== 1 ? 's' : ''}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-sm">
                      {Object.entries(row.permissions).map(([product, level]) => (
                        <div key={product} className="py-1">
                          {formatProductName(product)}: <strong>{row.isSuperadmin ? 'Superadmin' : level}</strong>
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="iam-support-no-permissions text-gray-400">No permissions assigned</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (row) => row.id,
      width: '140px',
      align: 'center',
      render: (_v, row) => (
        <div className="iam-support-actions">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onAssignPermission(row)}>
                  <Edit className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit Permissions</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {!row.isSuperadmin ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={() => onPromoteToSuperadmin(row)}>
                    <Star className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Promote to Superadmin</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                    onClick={() => onPromoteToSuperadmin(row)}
                  >
                    <ShieldOff className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Remove Superadmin Status</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
  ], [onAssignPermission, onPromoteToSuperadmin]);

  return (
    <div className="iam-support-access-management">
      <div className="iam-support-table-card border-0 shadow-none rounded-xl overflow-hidden bg-white flex flex-col flex-1 min-h-0">
        <div className="flex justify-between items-center p-4 bg-white rounded-t-xl">
          <div className="flex-1 flex justify-start items-center gap-2">
            <UserCog className="iam-support-icon" />
            <div className="text-base font-semibold leading-[19.2px] text-[#242424]">
              <div>
                <h3 className="text-base font-semibold leading-[19.2px] tracking-tight">Users &amp; Permissions</h3>
                <p className="text-sm text-muted-foreground">
                  Manage user permissions and access levels for different products
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="iam-support-table-wrapper flex flex-col flex-1 min-h-0 p-4">
          <DataTable<User>
            className="flex-1 min-h-0"
            maxHeight="100%"
            columns={dataTableColumns}
            data={users}
            loading={loading}
            emptyMessage={searchTerm ? 'No users found matching your search' : 'No users available'}
            stickyHeader
            stickyFooter
            pagination={pagination}
            showActions={false}
          />
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
